import { ArrowRight, BookOpen } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { PageHeader } from '../../components/common/PageHeader';
import { getEmployee, getEmployees, getTransactions } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { getPayrollSummary } from '../../utils/payroll';

const CURRENT_PERIOD = new Date().toISOString().slice(0, 7);

export function PaymentLedgerPage() {
  const { user } = useAuth();
  const { activeTenant, activeTenantId } = useTenant();
  const { notify } = useToast();
  const currency = activeTenant?.settings?.currency || 'KES';
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [period, setPeriod] = useState(CURRENT_PERIOD);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activeTenantId) return;
    setLoading(true);
    try {
      const [loadedEmployees, nextTransactions] = await Promise.all([getEmployees(activeTenantId), getTransactions(activeTenantId)]);
      let nextEmployees = loadedEmployees;
      const currentEmail = String(user?.email || '').trim().toLowerCase();
      const hasCurrentUser = nextEmployees.some((employee) => String(employee.email || '').trim().toLowerCase() === currentEmail && currentEmail);
      if (user && !hasCurrentUser) {
        const savedCurrentUser = await getEmployee(activeTenantId, 'current-user');
        if (savedCurrentUser) nextEmployees = [savedCurrentUser, ...nextEmployees];
      }
      setEmployees(nextEmployees);
      setPayments(nextTransactions.filter((item) => item.category === 'salary'));
    } catch (error) {
      console.error('Unable to load payment ledger', error);
      notify('Unable to load payment ledger. Try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, notify, user?.email]);

  useEffect(() => { load().catch((error) => console.error(error)); }, [load]);

  const rows = useMemo(() => {
    const currentEmail = String(user?.email || '').trim().toLowerCase();
    const hasCurrent = employees.some((employee) => String(employee.email || '').trim().toLowerCase() === currentEmail && currentEmail);
    const options = hasCurrent || !user ? employees : [{ id: 'current-user', fullName: user.displayName || user.email || 'Current user', email: user.email || '', salary: 0 }, ...employees];
    return options.map((employee) => ({ ...employee, payroll: getPayrollSummary(employee, period, payments) }));
  }, [employees, payments, period, user]);

  const totals = useMemo(() => rows.reduce((summary, row) => ({
    salary: summary.salary + row.payroll.salary,
    due: summary.due + row.payroll.totalDue,
    carried: summary.carried + row.payroll.carriedForward,
    paid: summary.paid + row.payroll.paidToDate,
    pending: summary.pending + row.payroll.remaining,
  }), { salary: 0, due: 0, carried: 0, paid: 0, pending: 0 }), [rows]);

  return (
    <div className="employees-page payroll-page payment-ledger-page">
      <PageHeader title="Payment Ledger" description="A complete view of salary obligations, payments and carried-forward balances." action={<div className="payroll-header-actions"><label className="ledger-period-picker">Period<input type="month" value={period} onChange={(event) => setPeriod(event.target.value)} /></label><Button to="/dashboard/hr/payroll/pay-salaries" icon={ArrowRight}>Pay salaries</Button></div>} />
      {loading ? <LoadingState label="Loading payment ledger..." /> : rows.length ? (
        <>
          <section className="ledger-overview-grid"><article><span>Current monthly payroll</span><strong>{formatCurrency(totals.salary, currency)}</strong><small>Current salaries for {period}</small></article><article><span>Payroll due through period</span><strong>{formatCurrency(totals.due, currency)}</strong><small>From each employee’s payroll start month</small></article><article className="ledger-overview-warning"><span>Pending payroll</span><strong>{formatCurrency(totals.pending, currency)}</strong><small>Still owed after all recorded payments</small></article><article><span>Paid to date</span><strong>{formatCurrency(totals.paid, currency)}</strong><small>All payments through {period}</small></article></section>
          <section className="payroll-balance-card"><div className="incomeTableHeader"><div><h6>Employee payment ledger</h6><p>Balances use each employee’s salary and accumulate from the first recorded payroll month.</p></div><span>{period}</span></div><div className="incomeTableWrap"><table className="incomeRecordsTable"><thead><tr><th>Employee</th><th>Current salary</th><th>Carried forward</th><th>Total due</th><th>Paid to date</th><th>Balance</th><th>Position</th></tr></thead><tbody>{rows.map((row) => <tr key={row.id}><td><strong>{row.fullName}</strong><small className="ledger-employee-meta">{row.jobTitle || row.email || 'Employee'}</small></td><td>{formatCurrency(row.payroll.salary, currency)}</td><td>{formatCurrency(row.payroll.carriedForward, currency)}</td><td>{formatCurrency(row.payroll.totalDue, currency)}</td><td>{formatCurrency(row.payroll.paidToDate, currency)}</td><td><strong>{formatCurrency(row.payroll.remaining, currency)}</strong></td><td><span className={`ledger-status ${row.payroll.remaining ? 'is-pending' : 'is-paid'}`}>{row.payroll.remaining ? (row.payroll.paidThisPeriod ? 'Partially paid' : 'Pending') : 'Fully paid'}</span></td></tr>)}</tbody></table></div></section>
        </>
      ) : <EmptyState title="No employees yet." message="Add employees before using the payment ledger." icon={BookOpen} />}
    </div>
  );
}
